/**
 * **可折叠卡片** —— 面板里的一组内容（内核件）。
 *
 * ## 为什么是 `.ts` 渲染函数，而不是 `.vue`
 *
 * ⚠️ 这是**被验证链逼出来的**：纯逻辑套件（`verify:print` 之类）用 esbuild
 * 打包，**不认 `.vue`**。而打印包的 `panels.ts` 要用这张卡片 —— 于是
 * "包里的 `.ts` 不许 import `.vue`"就成了这条链上的一条硬规矩
 * （包自己的编辑器也是 `.ts` + `h()`，同一个原因）。
 *
 * 内核里其它组件都是 `.vue`（它们只被 app 与 vite 打包的套件用到）；
 * 这一个必须两边都能用，所以写成 `defineComponent` + 渲染函数。
 *
 * ## 三条规矩
 *
 * ① **默认收起**（`open` 不给就是收起）：面板一共就那么高，铺开等于
 *    每次都要滚一遍找东西。
 * ② **整条标题可点**，不只是那个小三角 —— 目标大一点，少一次"点偏了"。
 * ③ **开合状态归调用方**（`v-model:open`）：同一个面板在不同窗口里开着
 *    不同的卡片是正常的，状态属于那个窗口，不属于卡片。
 *
 * ⚠️ 收起时**不渲染内容**（而不是 CSS 隐藏）：面板里的控件都带 `data-*`
 * 标记，验证脚本按"在不在"断言卡片开合的语义，`display: none` 的话
 * 它们仍然在 DOM 里，那条断言就失去意义了。
 */
import { defineComponent, h, type PropType, type VNode } from 'vue'

const CARD_CLASS = 'np-card'
const STYLE_ID = 'np-panel-card-styles'

/** 样式只插一次（各组件的样式都这么挂，见 `domains/print/styles.ts`） */
function ensureStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
.${CARD_CLASS} {
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.08));
  border-radius: var(--np-radius-s, 4px);
  margin-bottom: 4px;
  overflow: hidden;
}
.${CARD_CLASS}__head {
  display: flex; align-items: center; gap: 5px;
  padding: 4px 6px; cursor: pointer; user-select: none;
  background: var(--np-surface-bar-soft, rgba(255,255,255,0.03));
}
.${CARD_CLASS}.is-open .${CARD_CLASS}__head {
  border-bottom: 1px solid var(--np-border-soft, rgba(255,255,255,0.08));
}
.${CARD_CLASS}__caret { display: inline-flex; width: 9px; color: var(--np-panel-dim, #9a9a9a); }
.${CARD_CLASS}__label { font-weight: 500; }
.${CARD_CLASS}__badge { color: var(--np-panel-dim, #9a9a9a); font-variant-numeric: tabular-nums; }
.${CARD_CLASS}__spacer { flex: 1 1 auto; }
.${CARD_CLASS}__body { padding: 4px 6px 6px; }
`
  document.head.appendChild(style)
}

export const NpPanelCard = defineComponent({
  name: 'NpPanelCard',
  props: {
    label: { type: String, required: true },
    hint: { type: String, default: '' },
    /** 展开着吗（缺省 = 收起，见文件头 ①） */
    open: { type: Boolean, default: false },
    badge: { type: String, default: '' },
  },
  emits: ['update:open'],
  setup(props, { emit, slots }) {
    ensureStyles()
    return (): VNode => {
      const caret = props.open ? 'M1.5 3.2 L5 6.8 L8.5 3.2' : 'M3.2 1.5 L6.8 5 L3.2 8.5'
      return h(
        'section',
        {
          class: [CARD_CLASS, props.open ? 'is-open' : 'is-closed'],
          'data-np-card': props.label,
          'data-np-open': String(props.open),
        },
        [
          h(
            'div',
            {
              class: `${CARD_CLASS}__head`,
              role: 'button',
              tabindex: 0,
              'aria-expanded': String(props.open),
              title: props.hint || props.label,
              onClick: () => emit('update:open', !props.open),
              onKeydown: (ev: KeyboardEvent) => {
                if (ev.key !== 'Enter' && ev.key !== ' ') return
                ev.preventDefault()
                emit('update:open', !props.open)
              },
            },
            [
              h('span', { class: `${CARD_CLASS}__caret`, 'aria-hidden': 'true' }, [
                h(
                  'svg',
                  { viewBox: '0 0 10 10', fill: 'none', width: 9, height: 9 },
                  [
                    h('path', {
                      d: caret,
                      stroke: 'currentColor',
                      'stroke-width': 1.6,
                      'stroke-linecap': 'round',
                      'stroke-linejoin': 'round',
                    }),
                  ],
                ),
              ]),
              h('span', { class: `${CARD_CLASS}__label` }, props.label),
              props.badge ? h('span', { class: `${CARD_CLASS}__badge` }, props.badge) : null,
              h('span', { class: `${CARD_CLASS}__spacer` }),
            ],
          ),
          props.open ? h('div', { class: `${CARD_CLASS}__body` }, slots.default?.() ?? []) : null,
        ],
      )
    }
  },
})
